import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Save, RefreshCcw, Clock, ShieldCheck, Server, Send, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import { Link } from "react-router-dom";


const ALL_TIMEZONES = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function' 
  ? Intl.supportedValuesOf('timeZone') 
  : ["UTC", "Asia/Dhaka", "America/New_York", "Europe/London", "Asia/Tokyo", "Australia/Sydney"];

const EmailMarketingSMTPForm = () => {
  const [user] = useAuthState(auth);
  // SMTP State
  const [formData, setFormData] = useState({
    smtpUser: "",
    smtpPass: "",
    myEmail: "",
    displayName: "",
  });
  const [existingData, setExistingData] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null); // { type: 'success' | 'error', message: '' }
  const [smtpLoading, setSmtpLoading] = useState(false);

  // Gemini API State
  const [geminiData, setGeminiData] = useState({ geminiApiKey: "", geminiModel: "gemini-3.5-flash" });
  const [existingGeminiData, setExistingGeminiData] = useState(null);
  const [geminiStatus, setGeminiStatus] = useState(null);
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [isCustomGeminiModel, setIsCustomGeminiModel] = useState(false);

  // Cron State
  const [selectedHour, setSelectedHour] = useState("8");
  const [manualTime, setManualTime] = useState("08:00");
  const [useManual, setUseManual] = useState(false);
  const [cronTimezone, setCronTimezone] = useState("UTC");
  const [cronSchedule, setCronSchedule] = useState("");
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  // Build cron from hour or manual time
  const buildCronFromHour = (hour) => `0 ${hour} * * *`;
  const buildCronFromManual = (time) => {
    const [h, m] = time.split(":");
    return `${m} ${h} * * *`;
  };

  const fetchData = async () => {
    if (!user?.email) return;
    setInitialLoading(true);
    try {
      // Fetch SMTP
      const smtpRes = await axios.get(`https://emailserverjuly.shahlimon.online/email-merketing-smpt?ownerEmail=${user.email}`).catch(() => ({ data: [] }));
      if (smtpRes.data && smtpRes.data.length > 0) {
        setExistingData(smtpRes.data[0]);
        setFormData(smtpRes.data[0]);
      }

      // Fetch Gemini
      const geminiRes = await axios.get(`https://emailserverjuly.shahlimon.online/gemini-api-config?ownerEmail=${user.email}`).catch(() => ({ data: [] }));
      if (geminiRes.data && geminiRes.data.length > 0) {
        setExistingGeminiData(geminiRes.data[0]);
        const fetchedModel = geminiRes.data[0].geminiModel || "gemini-3.5-flash";
        setGeminiData({ 
          geminiApiKey: geminiRes.data[0].geminiApiKey || "",
          geminiModel: fetchedModel
        });
        const predefinedModels = ["gemini-3.5-flash", "gemini-3.1-flash-lite", "gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemma-4-31b-it"];
        if (!predefinedModels.includes(fetchedModel)) {
          setIsCustomGeminiModel(true);
        }
      }

      // Fetch Cron
      const cronRes = await axios.get(`https://emailserverjuly.shahlimon.online/api/cron-config?ownerEmail=${user.email}`).catch(() => ({ data: {} }));
      const schedule = cronRes.data.cronSchedule || "0 8 * * *";
      const timezone = cronRes.data.cronTimezone || "UTC";

      setCronTimezone(timezone);
      const parts = schedule.split(" ");
      if (parts.length >= 2) {
        const minute = parts[0];
        const hour = parts[1];
        setSelectedHour(hour);
        setManualTime(`${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`);
      }
      setCronSchedule(schedule);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchData();
    }
  }, [user]);

  // SMTP Handlers
  const handleSmtpChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSmtpSubmit = async (e) => {
    e.preventDefault();
    setSmtpLoading(true);
    setSmtpStatus(null);

    try {
      if (existingData) {
        await axios.put(`https://emailserverjuly.shahlimon.online/email-merketing-smpt/${existingData._id}`, { ...formData, ownerEmail: user?.email });
        setSmtpStatus({ type: 'success', message: "SMTP configuration updated securely." });
      } else {
        await axios.post("https://emailserverjuly.shahlimon.online/add-email-merketing-smpt", { ...formData, ownerEmail: user?.email });
        setSmtpStatus({ type: 'success', message: "SMTP configuration saved securely." });
        fetchData(); // Refresh to get existingData ID
      }
    } catch (error) {
      setSmtpStatus({ type: 'error', message: "Failed to save SMTP configuration." });
    } finally {
      setSmtpLoading(false);
      setTimeout(() => setSmtpStatus(null), 5000);
    }
  };

  // Gemini Handlers
  const handleGeminiChange = (e) => {
    if (e.target.name === "geminiModelSelect") {
      if (e.target.value === "custom") {
        setIsCustomGeminiModel(true);
        setGeminiData({ ...geminiData, geminiModel: "" });
      } else {
        setIsCustomGeminiModel(false);
        setGeminiData({ ...geminiData, geminiModel: e.target.value });
      }
    } else {
      setGeminiData({ ...geminiData, [e.target.name]: e.target.value });
    }
  };

  const handleGeminiSubmit = async (e) => {
    e.preventDefault();
    setGeminiLoading(true);
    setGeminiStatus(null);

    try {
      if (existingGeminiData) {
        await axios.put(`https://emailserverjuly.shahlimon.online/gemini-api-config/${existingGeminiData._id}`, { ...geminiData, ownerEmail: user?.email });
        setGeminiStatus({ type: 'success', message: "Gemini API configuration updated securely." });
      } else {
        await axios.post("https://emailserverjuly.shahlimon.online/add-gemini-api-config", { ...geminiData, ownerEmail: user?.email });
        setGeminiStatus({ type: 'success', message: "Gemini API configuration saved securely." });
        fetchData(); // Refresh to get ID
      }
    } catch (error) {
      setGeminiStatus({ type: 'error', message: "Failed to save Gemini API configuration." });
    } finally {
      setGeminiLoading(false);
      setTimeout(() => setGeminiStatus(null), 5000);
    }
  };

  // Cron Handlers
  const handleHourChange = (e) => {
    const hour = e.target.value;
    setSelectedHour(hour);
    setCronSchedule(buildCronFromHour(hour));
  };

  const handleManualTimeChange = (e) => {
    const time = e.target.value;
    setManualTime(time);
    setCronSchedule(buildCronFromManual(time));
  };

  const handleCronSubmit = async (e) => {
    e.preventDefault();
    setCronLoading(true);
    setCronStatus(null);
    try {
      await axios.post("https://emailserverjuly.shahlimon.online/api/cron-config", {
        cronSchedule,
        cronTimezone,
        ownerEmail: user?.email,
      });
      setCronStatus({ type: 'success', message: "Automation schedule updated successfully." });
    } catch (error) {
      setCronStatus({ type: 'error', message: "Failed to update automation schedule." });
    } finally {
      setCronLoading(false);
      setTimeout(() => setCronStatus(null), 5000);
    }
  };

  const StatusAlert = ({ status }) => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className={`d-flex align-items-center p-3 mb-4 rounded-3 ${status.type === 'success' ? 'bg-success bg-opacity-10 border border-success border-opacity-25' : 'bg-danger bg-opacity-10 border border-danger border-opacity-25'}`}
        >
          {status.type === 'success' ? (
            <CheckCircle2 className="text-success me-3 flex-shrink-0" size={20} />
          ) : (
            <AlertCircle className="text-danger me-3 flex-shrink-0" size={20} />
          )}
          <span className={status.type === 'success' ? 'text-success' : 'text-danger'} style={{ fontSize: '0.9rem' }}>
            {status.message}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 mb-5" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-info" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading Settings...</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="container-fluid mb-5 mt-3"
      style={{ maxWidth: '1400px' }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="d-flex align-items-center gap-4">
          <Link 
            to="/" 
            className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px', borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)' }}
            title="Back to Dashboard"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h4 className="fw-bold text-white mb-1 d-flex align-items-center" style={{ letterSpacing: '-0.5px' }}>
              <Server className="me-3 text-info" size={28} strokeWidth={2.5} />
              System Configuration
            </h4>
            <p className="text-gray-400 mb-0 ms-5 d-none d-md-block" style={{ fontSize: '0.9rem' }}>Manage email server credentials and automation schedules</p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-dark border border-gray-700 text-gray-300 d-flex align-items-center shadow-sm px-3 py-2"
          onClick={fetchData}
          style={{ transition: 'all 0.2s ease', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#d1d5db'; }}
        >
          <RefreshCcw className="me-2" size={16} />
          Refresh Data
        </button>
      </div>

      <div className="row g-4">
        {/* Left Column: SMTP & Gemini */}
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4 h-100">
            {/* SMTP Configuration Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-grow-1"
            >
          <div className="premium-card h-100">
            <div className="card-glow-effect grad-blue" />
            <div className="p-4 border-bottom border-gray-800 d-flex align-items-center position-relative z-1">
              <div className="icon-glow-box grad-blue me-3" style={{ width: '40px', height: '40px' }}>
                <ShieldCheck size={20} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h6 className="fw-bold mb-0 text-white tracking-wider text-uppercase small">SMTP Credentials</h6>
                <span className="text-gray-500" style={{ fontSize: '0.75rem' }}>Secure email delivery configuration</span>
              </div>
            </div>
            
            <div className="p-4 position-relative z-1 text-center py-5">
              <Server size={48} className="text-info opacity-50 mb-3" />
              <h5 className="fw-bold text-white mb-3">Upgraded SMTP System</h5>
              <p className="text-gray-400 small mb-4">
                You can now connect and manage multiple 3rd-party email providers securely using the new Custom SMTP settings.
              </p>
              <Link 
                to="/admin/custom-smtp" 
                className="btn premium-btn grad-blue d-inline-flex align-items-center"
              >
                <Server className="me-2" size={18} />
                Manage Custom SMTP
              </Link>
            </div>
          </div>
            </motion.div>
            
            {/* Gemini API Config Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex-grow-0"
            >
              <div className="premium-card">
                <div className="card-glow-effect grad-purple" style={{ bottom: '-50px', right: '-50px', top: 'auto', left: 'auto' }} />
                <div className="p-4 border-bottom border-gray-800 d-flex align-items-center position-relative z-1">
                  <div className="icon-glow-box grad-purple me-3" style={{ width: '40px', height: '40px' }}>
                    <Server size={20} className="text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0 text-white tracking-wider text-uppercase small">Gemini API Config</h6>
                    <span className="text-gray-500" style={{ fontSize: '0.75rem' }}>AI generation credentials</span>
                  </div>
                </div>
                
                <div className="p-4 position-relative z-1">
                  <StatusAlert status={geminiStatus} />
                  
                  <form onSubmit={handleGeminiSubmit}>
                    <div className="mb-4">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">API Key</label>
                      <input
                        type="password"
                        name="geminiApiKey"
                        value={geminiData.geminiApiKey}
                        onChange={handleGeminiChange}
                        className="form-control premium-input"
                        placeholder="Enter Gemini API Key"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">Gemini Model</label>
                      <select
                        name="geminiModelSelect"
                        value={isCustomGeminiModel ? "custom" : geminiData.geminiModel}
                        onChange={handleGeminiChange}
                        className="form-select premium-input mb-2"
                        required
                      >
                        <option value="gemini-3.5-flash">gemini-3.5-flash</option>
                        <option value="gemini-3.1-flash-lite">gemini-3.1-flash-lite</option>
                        <option value="gemini-3-flash-preview">gemini-3-flash-preview</option>
                        <option value="gemini-3.1-pro-preview">gemini-3.1-pro-preview</option>
                        <option value="gemma-4-31b-it">gemma-4-31b-it</option>
                        <option value="custom">Custom (Type your own)</option>
                      </select>
                      
                      {isCustomGeminiModel && (
                        <input
                          type="text"
                          name="geminiModel"
                          value={geminiData.geminiModel}
                          onChange={handleGeminiChange}
                          className="form-control premium-input"
                          placeholder="Type custom model name"
                          required
                        />
                      )}
                    </div>

                    <div className="pt-3 border-top border-gray-800 d-flex justify-content-end">
                      <button 
                        type="submit" 
                        className="btn premium-btn grad-purple d-flex align-items-center"
                        disabled={geminiLoading}
                      >
                        {geminiLoading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                          <Save className="me-2" size={18} />
                        )}
                        {existingGeminiData ? "Update Config" : "Save Config"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Column: Cron Schedule Card */}
        <div className="col-12 col-xl-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="h-100"
          >
          <div className="premium-card h-100">
            <div className="card-glow-effect grad-purple" style={{ right: 'auto', left: '-50px' }} />
            <div className="p-4 border-bottom border-gray-800 d-flex align-items-center position-relative z-1">
              <div className="icon-glow-box grad-purple me-3" style={{ width: '40px', height: '40px' }}>
                <Clock size={20} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h6 className="fw-bold mb-0 text-white tracking-wider text-uppercase small">Automated Campaign Schedule</h6>
                <span className="text-gray-500" style={{ fontSize: '0.75rem' }}>Daily cron job timing settings</span>
              </div>
            </div>
            
            <div className="p-4 position-relative z-1">
              <StatusAlert status={cronStatus} />

              <form onSubmit={handleCronSubmit}>
                <div className="mb-4">
                  <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">Time Input Mode</label>
                  <select
                    className="form-select premium-input"
                    value={useManual ? "manual" : "hour"}
                    onChange={(e) => setUseManual(e.target.value === "manual")}
                  >
                    <option value="hour">Hour Based (Simple)</option>
                    <option value="manual">Manual Time (HH:MM)</option>
                  </select>
                </div>

                <div className="row">
                  {!useManual ? (
                    <div className="col-md-6 mb-4">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">Select Hour (Daily)</label>
                      <select
                        className="form-select premium-input"
                        value={selectedHour}
                        onChange={handleHourChange}
                      >
                        {Array.from({ length: 24 }).map((_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, "0")}:00
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="col-md-6 mb-4">
                      <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">Select Time (HH:MM)</label>
                      <input
                        type="time"
                        className="form-control premium-input"
                        value={manualTime}
                        onChange={handleManualTimeChange}
                      />
                    </div>
                  )}

                  <div className="col-md-6 mb-4">
                    <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase">Timezone</label>
                    <select
                      className="form-select premium-input"
                      value={cronTimezone}
                      onChange={(e) => setCronTimezone(e.target.value)}
                    >
                      {ALL_TIMEZONES.map(tz => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4 p-3 rounded-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                  <label className="form-label text-gray-400 small fw-semibold tracking-wider text-uppercase mb-2 d-flex align-items-center">
                    <Send size={14} className="me-2 text-info" />
                    Generated Cron Expression
                  </label>
                  <code className="d-block w-100 p-2 rounded bg-dark text-info fs-5 text-center" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                    {cronSchedule}
                  </code>
                </div>

                <div className="pt-3 border-top border-gray-800 d-flex justify-content-end">
                  <button 
                    type="submit" 
                    className="btn premium-btn grad-purple d-flex align-items-center"
                    disabled={cronLoading}
                  >
                    {cronLoading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : (
                      <Save className="me-2" size={18} />
                    )}
                    Save Schedule
                  </button>
                </div>
              </form>
            </div>
          </div>
        </motion.div>

        </div>
      </div>

      <style jsx="true">{`
        /* Premium Dark Theme Styles */
        .premium-card {
          background: #111827;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 1.25rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          position: relative;
          transition: all 0.3s ease;
          overflow: hidden;
        }
        
        .premium-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%);
          pointer-events: none;
          z-index: 0;
        }

        .icon-glow-box {
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 16px -4px rgba(0,0,0,0.3);
        }

        .card-glow-effect {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.15;
          top: -50px;
          right: -50px;
          z-index: 0;
          pointer-events: none;
        }

        /* Inputs */
        .premium-input {
          background-color: rgba(0, 0, 0, 0.2) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          color: #f3f4f6 !important;
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          transition: all 0.2s ease;
        }
        .premium-input:focus {
          background-color: rgba(0, 0, 0, 0.3) !important;
          border-color: rgba(56, 189, 248, 0.5) !important;
          box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1) !important;
        }
        .premium-input::placeholder {
          color: #6b7280;
        }
        
        /* Select specific fixing for dark mode */
        select.premium-input {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
        }
        select.premium-input option {
          background-color: #1f2937;
          color: #f3f4f6;
        }

        /* Buttons */
        .premium-btn {
          color: white !important;
          border: none;
          padding: 0.6rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: 500;
          letter-spacing: 0.025em;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }
        .premium-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
        }
        .premium-btn:active {
          transform: translateY(0);
        }

        /* Gradients */
        .grad-blue { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .grad-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }

        /* Typography & Colors */
        .text-gray-300 { color: #d1d5db; }
        .text-gray-400 { color: #9ca3af; }
        .text-gray-500 { color: #6b7280; }
        .border-gray-700 { border-color: rgba(255,255,255,0.1) !important; }
        .border-gray-800 { border-color: rgba(255,255,255,0.05) !important; }
        .tracking-wider { letter-spacing: 0.05em; }
      `}</style>
    </motion.div>
  );
};

export default EmailMarketingSMTPForm;
